import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Eye } from "lucide-react";
import { Link } from "react-router-dom";
import CTAButton from "./CTAButton";
import Navbar from "./Navbar";
import Footer from "./Footer";
import ProjectLightbox from "./ProjectLightbox";
import { usePortfolioProjects } from "@/hooks/usePortfolioProjects";

const COLORS = [
  { value: "bianco", label: "Bianco", hex: "#FFFFFF" },
  { value: "nero", label: "Nero", hex: "#1a1a1a" },
  { value: "grigio", label: "Grigio", hex: "#808080" },
  { value: "marrone", label: "Marrone", hex: "#8B4513" },
  { value: "beige", label: "Beige", hex: "#D2B48C" },
  { value: "verde", label: "Verde", hex: "#2E8B57" },
  { value: "blu", label: "Blu", hex: "#2563EB" },
  { value: "rosso", label: "Rosso", hex: "#DC2626" },
  { value: "antracite", label: "Antracite", hex: "#383838" },
  { value: "avorio", label: "Avorio", hex: "#FFFFF0" },
  { value: "bronzo", label: "Bronzo", hex: "#CD7F32" },
  { value: "corten", label: "Corten", hex: "#8B4000" },
  { value: "legno-naturale", label: "Effetto legno", hex: "#DEB887" },
  { value: "oro", label: "Oro", hex: "#DAA520" },
  { value: "argento", label: "Argento", hex: "#C0C0C0" },
];

interface SubcategoryPageLayoutProps {
  heading: string;
  subtitle: string;
  categoryKey: string;
  backTo: string;
  backLabel: string;
}

const SubcategoryPageLayout = ({ heading, subtitle, categoryKey, backTo, backLabel }: SubcategoryPageLayoutProps) => {
  const { projects, loading } = usePortfolioProjects(categoryKey, true);
  const [colorFilter, setColorFilter] = useState("");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Get unique colors from projects for this category
  const availableColors = COLORS.filter(c => projects.some(p => p.color === c.value));
  const filteredProjects = colorFilter ? projects.filter(p => p.color === colorFilter) : projects;

  return (
    <div className="overflow-x-hidden">
      <Navbar />

      {/* Hero */}
      <section className="section-light pt-32 pb-16 md:pt-40 md:pb-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link
              to={backTo}
              className="inline-flex items-center gap-2 text-section-light-foreground/50 hover:text-section-light-foreground text-sm font-body tracking-wider uppercase transition-colors duration-300 mb-10"
            >
              <ArrowLeft size={16} />
              {backLabel}
            </Link>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-section-light-foreground uppercase leading-[1.05] max-w-4xl"
          >
            {heading}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-6 text-section-light-foreground/50 font-body text-base md:text-lg max-w-xl"
          >
            {subtitle}
          </motion.p>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="section-light pb-24 md:pb-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Color filter */}
          {availableColors.length > 0 && (
            <div className="mb-8">
              <p className="text-section-light-foreground/50 font-body text-xs uppercase tracking-wider mb-3">Filtra per colore</p>
              <div className="flex flex-wrap gap-2 items-center">
                <button
                  onClick={() => setColorFilter("")}
                  className={`px-3 py-1.5 rounded-full text-xs font-body tracking-wider uppercase transition-all duration-200 border ${
                    !colorFilter
                      ? "border-accent bg-accent/10 text-section-light-foreground"
                      : "border-section-light-foreground/20 text-section-light-foreground/50 hover:border-section-light-foreground/40"
                  }`}
                >
                  Tutti
                </button>
                {availableColors.map((c) => (
                  <button
                    key={c.value}
                    onClick={() => setColorFilter(colorFilter === c.value ? "" : c.value)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-body tracking-wider transition-all duration-200 border ${
                      colorFilter === c.value
                        ? "border-accent bg-accent/10 text-section-light-foreground"
                        : "border-section-light-foreground/20 text-section-light-foreground/50 hover:border-section-light-foreground/40"
                    }`}
                  >
                    <span className="w-3 h-3 rounded-full border border-section-light-foreground/20" style={{ backgroundColor: c.hex }} />
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-square bg-section-light-foreground/10 rounded-sm" />
                  <div className="mt-4 h-4 w-2/3 bg-section-light-foreground/10 rounded" />
                  <div className="mt-2 h-3 w-1/2 bg-section-light-foreground/5 rounded" />
                </div>
              ))}
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-section-light-foreground/40 font-body text-base">
                {colorFilter ? "Nessun lavoro trovato per questo colore." : "Nessun lavoro caricato ancora per questa categoria."}
              </p>
              {!colorFilter && (
                <p className="text-section-light-foreground/30 font-body text-sm mt-2">
                  I progetti appariranno qui una volta pubblicati.
                </p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {filteredProjects.map((project, i) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.15 + i * 0.1 }}
                  className="group"
                >
                  <div
                    className="aspect-square overflow-hidden rounded-sm relative cursor-pointer"
                    onClick={() => { setLightboxIndex(i); setLightboxOpen(true); }}
                  >
                    <img
                      src={project.image_url}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-background/60 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col items-center justify-center gap-2">
                      <Eye size={28} className="text-foreground" />
                      <span className="text-foreground text-xs tracking-widest uppercase font-body">Ingrandisci</span>
                    </div>
                  </div>
                  <div className="mt-4">
                    <h3 className="font-heading text-base font-semibold text-section-light-foreground uppercase tracking-wide">
                      {project.title}
                    </h3>
                    {project.description && (
                      <p className="mt-1 text-section-light-foreground/40 font-body text-sm">
                        {project.description}
                      </p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="section-light pb-24 md:pb-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto text-center">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="font-heading text-2xl md:text-4xl font-bold text-section-light-foreground uppercase"
          >
            Hai un progetto in mente?
          </motion.h2>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-8"
          >
            <CTAButton className="cta-button-light hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-300" />
          </motion.div>
        </div>
      </section>

      <ProjectLightbox
        projects={filteredProjects}
        initialIndex={lightboxIndex}
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
      />
      <Footer />
    </div>
  );
};

export default SubcategoryPageLayout;
